import { useId, useRef } from "react";
import type { ProductVendor } from "@models/product";
import { currencyFormatter } from "@utils/formatter";

interface OtherVendorsProps {
    // The other offerings of the sku on screen (otherOfferings), cheapest first.
    offerings: ProductVendor[];
    onChoose: (offering: ProductVendor) => void;
    // While a cart write is out, as Add to Cart is.
    disabled: boolean;
}

// "Purchase from another vendor" (B-09, MVP per Sklar 2026-10-20). Choosing a vendor does not
// add anything: it makes that vendor the one the page's Add to Cart buys from, so the price,
// the Vendor row and every stock and refusal rule follow it. Each offering's stock is shown
// because it is what the shopper chooses between — this is a choice of supply, not a low-stock
// hint; an unknown stock (an older payload) shows none.
export const OtherVendors = ({
    offerings,
    onChoose,
    disabled,
}: OtherVendorsProps) => {
    const headingId = useId();
    const headingRef = useRef<HTMLParagraphElement>(null);

    if (offerings.length === 0) return null;

    return (
        <div className="flex flex-col gap-1">
            {/* Focus lands here after a choice: the chosen vendor's button leaves the list
                (it is now the one bought from), and focus must not drop to the body. */}
            <p
                id={headingId}
                ref={headingRef}
                tabIndex={-1}
                className="font-bold text-neutral focus:outline-none"
            >
                Purchase from another vendor:
            </p>
            <ul aria-labelledby={headingId} className="flex flex-col gap-1">
                {offerings.map((offering) => (
                    <li key={offering.id} className="text-sm text-neutral">
                        <button
                            type="button"
                            onClick={() => {
                                onChoose(offering);
                                headingRef.current?.focus();
                            }}
                            disabled={disabled}
                            className="link link-primary font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {offering.vendorName}
                        </button>{" "}
                        <span>{currencyFormatter.format(offering.price)}</span>
                        {offering.availableQuantity !== undefined && (
                            <span className="text-neutral/70">
                                {` · ${offering.availableQuantity} available`}
                            </span>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};
